import { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import useBranding from '../store/branding';
import useMount from '../hooks/useMount';
import { translate } from '../utils/translate';
import { DEFAULT_TITLE } from '../configs';
import useAuth from '../store/auth';

const Metatags: FC = () => {
  const location = useLocation();
  const [brandingState, brandingActions] = useBranding();
  const [authState] = useAuth();
  const logoImage = brandingState?.settings?.favicon;

  useMount(() => {
    brandingActions.get();
  });

  useEffect(() => {
    document.title = brandingState?.settings?.title || DEFAULT_TITLE;
  }, [brandingState?.settings?.title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect((): void => {
    const existingScript = document.querySelector('script[type="application/ld+json"]');

    if (authState?.data?.affiliate?.id) {
      if (existingScript) {
        existingScript.remove();
      }
      return;
    }

    if (!existingScript && brandingState?.settings) {
      const cleanDescription = brandingState.settings.description?.replace(/<\/?[^>]+(>|$)/g, '') || '';

      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Brand',
        name: brandingState.settings.title || DEFAULT_TITLE,
        url: `${window.location.origin}${location.pathname}`,
        logo: logoImage,
        image: logoImage,
        description: cleanDescription,
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.innerHTML = JSON.stringify(schemaData);
      document.head.insertBefore(script, document.head.firstChild);
    }
  }, [authState?.data?.affiliate?.id, brandingState?.settings, logoImage, location.pathname]);

  // Google Tag Manager Script
  useEffect(() => {
    if (brandingState?.settings?.meta?.googleTagManager) {
      const gtmID = brandingState.settings.meta.googleTagManager;
      const script = document.createElement('script');
      script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmID}');
    `;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }, [brandingState?.settings?.meta?.googleTagManager]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="apple-touch-icon" href={brandingState?.settings?.favicon} />
        <link rel="icon" href={brandingState?.settings?.favicon} type="image/x-icon" />
        <link rel="shortcut icon" href={brandingState?.settings?.favicon} type="image/x-icon" />
        <title>
          {brandingState?.status === 'loading'
            ? translate('meta_loading')
            : brandingState?.settings?.title || DEFAULT_TITLE}
        </title>
        <meta name="application-name" content={brandingState?.settings?.title || DEFAULT_TITLE} />
        <meta name="description" content={brandingState?.settings?.description} />
      </Helmet>
    </>
  );
};

export default Metatags;
